<template>
  <a :href="link" class="card">
    <div class="card-inner">
      <!-- Title: sits in a flex row that shrinks but never pushes the icon -->
      <p
        ref="title"
        class="title"
        :style="{ fontSize: titleFontSize + 'px' }"
      >{{ title }}</p>

      <!-- Icon -->
      <div v-if="icon !== ''" class="icon-wrap">
        <div
          class="icon"
          role="img"
          :aria-label="title"
          :style="{
            backgroundImage: 'url(' + require('@/assets/icons/' + icon + '.svg') + ')'
          }"
        ></div>
      </div>

      <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
    </div>
  </a>
</template>

<script>
export default {
  name: "ProfileCard",
  props: {
    title:    { type: String, required: true, default: "Info" },
    subtitle: { default: "" },
    icon:     { default: "" },
    link:     { default: "" }
  },
  data() {
    return {
      titleFontSize:    20,
      maxTitleFontSize: 20,
      minTitleFontSize: 11,
      resizeObserver:   null,
      fitPending:       false
    };
  },
  mounted() {
    this.$nextTick(this.fitTitle);
    this.setupResizeObserver();
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
  },
  watch: {
    title()    { this.$nextTick(this.fitTitle); },
    subtitle() { this.$nextTick(this.fitTitle); }
  },
  methods: {
    setupResizeObserver() {
      if (!window.ResizeObserver || !this.$el) return;
      this.resizeObserver = new ResizeObserver(() => {
        // Debounce to avoid a storm of reflows while the grid is settling
        if (this.fitPending) return;
        this.fitPending = true;
        requestAnimationFrame(() => {
          this.fitPending = false;
          this.fitTitle();
        });
      });
      this.resizeObserver.observe(this.$el);
    },

    /**
     * Iterates one font-size step per tick so Vue re-renders between each
     * check — the only correct way to measure after reactive style changes.
     */
    async fitTitle() {
      const el = this.$refs.title;
      if (!el) return;

      // Reset to max before measuring
      this.titleFontSize = this.maxTitleFontSize;
      await this.$nextTick();

      while (this.titleFontSize > this.minTitleFontSize) {
        const overflows =
          el.scrollHeight > el.clientHeight + 1 ||
          el.scrollWidth  > el.clientWidth  + 1;
        if (!overflows) break;

        this.titleFontSize -= 1;
        await this.$nextTick(); 
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Card shell
.card {
  display: block;
  border: 1.5px solid #00a9b3;
  border-radius: 16px;
  aspect-ratio: 1 / 1;
  width: 100%;
  background: #f2fbfc;
  text-decoration: none;
  color: inherit;
  container-type: inline-size; /* enables cqi units inside */
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover,
  &:focus-visible {
    box-shadow: 0 4px 14px rgba(0, 169, 179, 0.25);
    transform: translateY(-2px);
    outline: none;
  }
}

// Card inside
.card-inner {
  width: 100%;
  height: 100%;
  padding: clamp(6px, 5cqi, 12px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;   /* vertically centre the whole group */
  gap: clamp(4px, 3cqi, 8px);
}

.title {
  flex: 1 1 0;
  min-height: 0;              // flexbox actually shrink

  width: 100%;
  margin: 0;
  padding: 0 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-family: "KlinicSlab", serif;
  font-weight: normal;
  line-height: 1.15;
  text-align: center;
  overflow: hidden;

  /* Prefer keeping words together; break only when truly necessary */
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  text-wrap: balance;
}

// Icon wrap
.icon-wrap {
  flex: 0 0 auto;             /* never grows or shrinks */
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  width:  clamp(36px, 32cqi, 68px);
  height: clamp(36px, 32cqi, 68px);
  border-radius: clamp(8px, 6cqi, 14px);
  background-color: rgba(229, 115, 120, 0.09);
  background-repeat: no-repeat;
  background-size: 65%;
  background-position: center;
}

.subtitle {
  flex: 0 0 auto;
  margin: 0;
  font-size: clamp(10px, 9cqi, 13px);
  color: #666;
  text-align: center;
}
</style>